import { defineStore } from 'pinia'
import { Recap, RecapResponse } from '../types/recap'
import { Http } from '@capacitor-community/http'
import { LanguageChoice } from '../i18n'
import { ref, Ref } from 'vue'
import { StoreRecapImageUpload } from '../models/StoreRecapImagesModel'
import {
  DatabaseStoreRecapDefinition,
  StoreRecapDefinitionsModel,
} from '../models/StoreRecapDefinitionsModel'
import { StoreRecapResponsesModel } from '../models/StoreRecapResponsesModel'
import { useUserStore } from './useUserStore'
import { Nullish } from '../types/utility'
import { captureException } from '@sentry/vue'
import { logInfo } from '../utils'

export const useStoreRecaps = defineStore<'storeRecaps', StoreRecapStore>('storeRecaps', () => {
  const recaps = ref<StoreRecaps>({})
  const recapMappings = ref<RecapMappings>({})

  const recapImages = ref<StoreRecapImages>([])

  const resetStoreState = () => {
    for (const key in recaps) {
      delete recaps.value[key]
    }
    recap_responses.value = []
  }

  const recap_responses = ref<RecapResponse[]>([])
  const { writeStoreRecapResponses, removeStoreResponsesForSyncedResponseWithStoreId } =
    StoreRecapResponsesModel()

  async function getStoreRecapsHash() {
    return await StoreRecapDefinitionsModel().getStoreRecapDefinitionHashes()
  }

  const setRecapsForStore = async (store_id: string) => {
    const defs = await StoreRecapDefinitionsModel().getRecapDefinitionsForStore(store_id)
    const lang = useUserStore().language
    //if result
    if (defs && defs.length) {
      for (const def of defs) {
        // Force the definition to load, even if it's already in the store
        recaps.value[`${store_id}-${def.recap_id}`] = {
          recapDefinition: JSON.parse(def.recap_definition) as Recap,
          store_id,
          language: lang,
        }
      }
    } else {
      console.error('No store recaps found for store', { store_id })
    }
  }

  async function setRecapsFromSync(
    syncRecaps: { recaps: any; venue_mappings: any },
    initialLanguage: LanguageChoice
  ) {
    if (syncRecaps) {
      // Sync the new recaps
      for (const key in syncRecaps.venue_mappings) {
        recapMappings.value[key] = syncRecaps.venue_mappings[key].map((id: any) => id.toString())
      }
      const keys = Object.keys(syncRecaps.recaps)
      keys.forEach((key: any) => {
        const recap = syncRecaps.recaps[key]
        const venueMappings = recapMappings.value[key]
        if (recap && venueMappings && venueMappings.length) {
          venueMappings.forEach((store_id: string) => {
            recaps.value[`${store_id}-${recap.id}`] = {
              recapDefinition: recap,
              store_id: store_id,
              language: initialLanguage,
            }
          })
        }
      })

      // Delete any recaps that aren't part of the newly synced data
      const originalRecapKeys = Object.keys(recaps)
      const recapKeys = Object.keys(recaps)
        .map((key) => key.split('-')[1])
        .filter((key, index, arr) => arr.indexOf(key) === index)
      recapKeys.forEach((key) => {
        const recap = syncRecaps.recaps[key]
        if (!recap) {
          originalRecapKeys.forEach((originalKey) => {
            if (originalKey.split('-')[1] === key) [delete recaps.value[originalKey]]
          })
        }
      })
    }
  }

  function setRecapsFromDatabase(
    databaseRecaps: DatabaseStoreRecapDefinition[],
    initialLanguage: LanguageChoice
  ) {
    databaseRecaps.forEach((recap) => {
      const definition = JSON.parse(recap.recap_definition) as Recap
      const storeIds = recap.store_ids.split(',')
      storeIds.forEach((store_id) => {
        recaps.value[`${store_id}-${recap.recap_id}`] = {
          recapDefinition: definition,
          store_id,
          language: initialLanguage,
        }
      })
    })
  }

  // Grab single recap
  async function setRecapFromDatabase(recap_id: string, store_id: string) {
    const def = await StoreRecapDefinitionsModel().getStoreRecapDefinition(recap_id)
    const lang = useUserStore().language
    if (def && def.length) {
      // Force the definition to load, even if it's already in the store
      recaps.value[`${store_id}-${recap_id}`] = {
        recapDefinition: JSON.parse(def[0].recap_definition) as Recap,
        store_id,
        language: lang,
      }
    } else {
      console.trace()
      console.error({ store_id, recap_id })
      captureException('Recap not found')
    }
  }

  function setRecapResponses(responses: RecapResponse[]) {
    recap_responses.value = responses
  }

  async function deleteRecapResponse(response: RecapResponse) {
    const responseIndex = recap_responses.value.findIndex(
      (res) =>
        res.store_id === response.store_id && res.events_recaps_id === response.events_recaps_id
    )
    if (responseIndex > -1) {
      await removeStoreResponsesForSyncedResponseWithStoreId(
        response.store_id!,
        response.events_recaps_id
      )
      recap_responses.value.splice(responseIndex, 1)
    }
  }

  /**
   * Fetches a recap definition. This method should only be used for testing recaps.
   */
  async function fetchRecap(id: string, store_id: string) {
    let origin = ''

    if (
      /https?:\/\/app\.neptune-(acceptance|staging|testing|qa|production)/.test(
        window.location.origin
      )
    ) {
      origin = window.location.origin.replace(/:\/\/app\./, '://global.')
    }

    const response = await Http.post({
      url: `${origin}/api/v1/recap_definition/show_with_params`,
      data: { id, store_id },
    }).catch(() => ({ data: null }))

    // The 0 here is a placeholder store ID so we don't accidentally overwrite data for a real recap.
    recaps.value[`0-${id}`] = {
      recapDefinition: response.data,
      store_id,
      language: 'en_US',
    }
  }

  async function saveRecapResponses(responses?: RecapResponse[]) {
    if (responses && responses.length) {
      await writeStoreRecapResponses(responses)
    } else {
      await writeStoreRecapResponses(recap_responses.value)
    }
  }

  async function saveRecap(recapDefinition: any, store_id: string, recap_id: string) {
    const response = await recapDefinition.getRecapResponse()
    if (response) {
      if (response && 'validationError' in response) {
        // Validation error, not a runtime error
        logInfo('debug', 'saveStoreRecap', `Validation error saving response for store ${store_id}`, { store_id })
        return { validationError: true }
      }
      const existingIndex = recap_responses.value?.findIndex(
        (res) => res.store_id === store_id && res.events_recaps_id === recap_id
      )
      if (existingIndex !== null && existingIndex > -1) {
        recap_responses.value.splice(existingIndex, 1, response)
      } else {
        recap_responses.value.push(response)
      }
      await saveRecapResponses([response])
      return true
    }
    return false
  }

  //remove recap
  const removeRecapFromStore = (
    recap_id: number | string | Nullish,
    store_id: number | string | Nullish
  ) => {
    delete recaps.value[`${store_id}-${recap_id}`]
  }

  return {
    recaps,
    recapImages,
    getStoreRecapsHash,
    recapMappings,
    recap_responses,
    setRecapsFromSync,
    setRecapsFromDatabase,
    setRecapFromDatabase,
    setRecapResponses,
    fetchRecap,
    taskId: ref('0'), // Added for type compatibility with the task Recap Store via Pinia
    resetStoreState,
    saveRecap,
    deleteRecapResponse,
    setRecapsForStore,
    removeRecapFromStore,
  }
})

type StoreRecaps = {
  [key: string]: {
    recapDefinition: Recap | null
    store_id: string
    language: LanguageChoice
  }
}

type RecapMappings = {
  [key: string]: string[]
}

type StoreRecapImages = { store_id: string; recap_id: string; image: StoreRecapImageUpload }[]

type StoreRecapStoreFunctions = {
  getStoreRecapsHash: () => Promise<{ [key: string]: string }>
  setRecapsFromSync: (
    syncRecaps: { recaps: any; venue_mappings: any },
    initialLanguage: LanguageChoice
  ) => Promise<void>
  setRecapsFromDatabase: (
    databaseRecaps: DatabaseStoreRecapDefinition[],
    initialLanguage: LanguageChoice
  ) => void
  setRecapFromDatabase: (recap_id: string, store_id: string) => Promise<void>
  setRecapResponses: (responses: RecapResponse[]) => void
  fetchRecap: (id: string, store_id: string) => Promise<void>
  saveRecap: (recapDefinition: any, store_id: string, recap_id: string) => Promise<{validationError: boolean} | boolean>
  deleteRecapResponse: (response: RecapResponse) => Promise<void>
  setRecapsForStore: (store_id: string) => Promise<void>
  removeRecapFromStore: (
    recap_id: number | string | Nullish,
    store_id: number | string | Nullish
  ) => void
  resetStoreState: () => void
}

type StoreRecapStoreData = {
  recaps: Ref<StoreRecaps>
  recapImages: Ref<StoreRecapImages>
  recapMappings: Ref<RecapMappings>
  recap_responses: Ref<RecapResponse[]>
}

export type StoreRecapStore = StoreRecapStoreData & StoreRecapStoreFunctions

export type StoreRecapStoreRefs = StoreRecapStoreData
